import React, { useState } from "react";
import { Edge, Node } from "@xyflow/react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGetAiAgentsQuery, useGetMembersQuery } from "@/store/rest_api";
import { CALL_FLOW_TEMPLATES, CallFlowTemplate, TemplateReferenceValues } from "@/util/callFlowTemplates";
import { CallFlowType } from "@/model/interface/CallFlowType";
import { useFeature } from "@/hooks/useFeature";
import { isProActionType } from "../nodes/actionTypeCatalog";
import { memberName } from "@/util/member";

export interface TemplateChoice {
  nodes: Node[];
  edges: Edge[];
  type: CallFlowType;
}

interface TemplateGalleryProps {
  onChoose: (choice: TemplateChoice | null) => void;
}

export default function TemplateGallery({ onChoose }: TemplateGalleryProps) {
  const { data: members = [] } = useGetMembersQuery({ status: undefined });
  const { data: aiAgents = [] } = useGetAiAgentsQuery();
  const hasRoutingSteps = useFeature("CALL_ROUTING_STEPS");
  const navigate = useNavigate();
  const [template, setTemplate] = useState<CallFlowTemplate | null>(null);
  const [references, setReferences] = useState<TemplateReferenceValues>({});

  function chooseMember(membershipId: string) {
    const member = members.find((candidate) => String(candidate.id) === membershipId);
    setReferences({
      ...references,
      membershipId,
      memberName: member ? memberName(member, "") : undefined,
    });
  }

  function toggleRingMember(membershipId: string) {
    const current = references.ringMemberIds ?? [];
    setReferences({
      ...references,
      ringMemberIds: current.includes(membershipId)
        ? current.filter((id) => id !== membershipId)
        : [...current, membershipId],
    });
  }

  function build(withReferences: TemplateReferenceValues) {
    if (!template) {
      return;
    }
    const { nodes, edges } = template.build(withReferences);
    onChoose({ nodes, edges, type: template.type });
  }

  function needsPro(candidate: CallFlowTemplate) {
    return !hasRoutingSteps && candidate.build({}).nodes.some((node) => isProActionType(node.data.actionType));
  }

  if (!template) {
    return (
      <div className="space-y-6 p-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Create a call flow</h1>
          <p className="text-muted-foreground">Start from a template, or build your own from scratch.</p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {CALL_FLOW_TEMPLATES.map((candidate) => {
            const isLocked = needsPro(candidate);
            return (
              <Card key={candidate.key} className="flex flex-col">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {candidate.name}
                    {isLocked && <Badge variant="secondary">Pro</Badge>}
                  </CardTitle>
                  <CardDescription>{candidate.description}</CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  {isLocked ? (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => navigate("/manage-organisation/plans")}
                    >
                      Upgrade to Pro
                    </Button>
                  ) : (
                    <Button
                      className="w-full"
                      onClick={() => {
                        if (candidate.references.length === 0) {
                          const { nodes, edges } = candidate.build({});
                          onChoose({ nodes, edges, type: candidate.type });
                          return;
                        }
                        setTemplate(candidate);
                      }}
                    >
                      Use this
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}

          <Card className="flex flex-col">
            <CardHeader>
              <CardTitle>Start from scratch</CardTitle>
              <CardDescription>An empty canvas, for a flow that does not match any template.</CardDescription>
            </CardHeader>
            <CardContent className="mt-auto">
              <Button variant="outline" className="w-full" onClick={() => onChoose(null)}>
                Start blank
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{template.name}</h1>
        <p className="text-muted-foreground">{template.description}</p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Who does this flow use?</CardTitle>
          <CardDescription>
            Fill these in now, or skip and set them on the canvas. The flow cannot be saved until they are set either
            way.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {template.references.includes("member") && (
            <div className="space-y-2">
              <Label htmlFor="template-member">Who to ring</Label>
              <Select value={references.membershipId ?? ""} onValueChange={chooseMember}>
                <SelectTrigger id="template-member">
                  <SelectValue placeholder="Pick a team member" />
                </SelectTrigger>
                <SelectContent>
                  {members.map((member) => (
                    <SelectItem key={member.id} value={String(member.id)}>
                      {memberName(member)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {template.references.includes("ringMembers") && (
            <div className="space-y-2">
              <Label>Who is in the team</Label>
              <div className="space-y-2 rounded-md border p-3">
                {members.map((member) => (
                  <div key={member.id} className="flex items-center gap-3">
                    <Checkbox
                      id={`ring-${member.id}`}
                      checked={(references.ringMemberIds ?? []).includes(String(member.id))}
                      onCheckedChange={() => toggleRingMember(String(member.id))}
                    />
                    <Label htmlFor={`ring-${member.id}`} className="font-normal">
                      {memberName(member)}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {template.references.includes("aiAgent") && (
            <div className="space-y-2">
              <Label htmlFor="template-agent">Which AI agent answers after hours</Label>
              <Select
                value={references.aiAgentId ?? ""}
                onValueChange={(aiAgentId) => setReferences({ ...references, aiAgentId })}
              >
                <SelectTrigger id="template-agent">
                  <SelectValue placeholder="Pick an AI agent" />
                </SelectTrigger>
                <SelectContent>
                  {aiAgents.map((agent) => (
                    <SelectItem key={agent.id} value={String(agent.id)}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex max-w-2xl flex-wrap justify-between gap-3">
        <Button variant="ghost" onClick={() => setTemplate(null)}>
          Back to templates
        </Button>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => build({})}>
            Set these up later
          </Button>
          <Button onClick={() => build(references)}>Continue</Button>
        </div>
      </div>
    </div>
  );
}
